import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  AiFormStatus,
  AiProcessExecutionLiveStatuses,
  AiProcessStepExecutionStatus,
  LoadStatus,
  RUNS_DEFAULT_PAGE_SIZE,
} from 'constants/aiProcessRuns';

import { computeRunToolStats } from './aiProcessRuns.selectors.utils';

const selectSlice = state => state.aiProcessRuns;

const emptyArray = Object.freeze([]);

const emptySet = Object.freeze(new Set());

// DEV-3064: count the streamed steps that have reached Completed for a run.
// SignalR step_completed events upsert into stepsByRunId, but never bump the
// run's completedStepsCount aggregate, so a live run's "X of Y steps" froze at
// the value captured by the REST load. Deriving the count from the streamed
// steps lets the live progress advance in real time.
const countStepsWithStatus = (slice, executionId, status) => {
  const bucket = slice.stepsByRunId[executionId];

  if (_.isNil(bucket)) {
    return 0;
  }

  return bucket.ids.reduce((count, id) => {
    const step = slice.stepsById[id];

    return step?.status === status ? count + 1 : count;
  }, 0);
};

const countCompletedSteps = (slice, executionId) =>
  countStepsWithStatus(slice, executionId, AiProcessStepExecutionStatus.COMPLETED);

const emptyFilters = Object.freeze({
  status: null,
  processId: null,
  startedAfter: null,
  startedBefore: null,
  skip: 0,
  take: RUNS_DEFAULT_PAGE_SIZE,
});

export const selectRunsByOwner = ownerId =>
  createSelector(selectSlice, slice => {
    const bucket = slice.runsByOwner[ownerId];

    if (_.isNil(bucket)) {
      return emptyArray;
    }

    return bucket.ids.map(id => slice.runsById[id]).filter(Boolean);
  });

export const selectRunsTotalCountByOwner = ownerId =>
  createSelector(selectSlice, slice => slice.runsByOwner[ownerId]?.totalCount ?? 0);

export const selectRunsLoadStatusByOwner = ownerId =>
  createSelector(selectSlice, slice => slice.runsByOwner[ownerId]?.status ?? LoadStatus.IDLE);

export const selectLiveRunsByOwner = ownerId =>
  createSelector(selectSlice, slice => {
    const bucket = slice.runsByOwner[ownerId];

    if (_.isNil(bucket)) {
      return emptyArray;
    }

    const liveItems = bucket.ids
      .map(id => slice.runsById[id])
      .filter(run => run && AiProcessExecutionLiveStatuses.has(run.status))
      .map(run => {
        // DEV-3064: prefer the live step-derived completed count, but never
        // regress below the REST snapshot (which may already reflect steps not
        // yet streamed into the store).
        const liveCompleted = countCompletedSteps(slice, run.id);
        const restCompleted = run.completedStepsCount ?? run.completedSteps ?? 0;
        const completedRun =
          liveCompleted > restCompleted ? { ...run, completedStepsCount: liveCompleted } : run;

        return _.isNil(slice.stepsByRunId[run.id])
          ? completedRun
          : {
              ...completedRun,
              runningStepCount: countStepsWithStatus(
                slice,
                run.id,
                AiProcessStepExecutionStatus.RUNNING,
              ),
            };
      });

    return _.orderBy(liveItems, ['startedAt'], ['desc']);
  });

export const selectStepStatusesByRun = executionId =>
  createSelector(selectSlice, slice =>
    (slice.stepsByRunId[executionId]?.ids ?? emptyArray)
      .map(id => slice.stepsById[id]?.status)
      .filter(Boolean),
  );

export const selectRunById = executionId =>
  createSelector(selectSlice, slice => slice.runsById[executionId] ?? null);

export const selectStepsByRun = executionId =>
  createSelector(selectSlice, slice => {
    const bucket = slice.stepsByRunId[executionId];

    if (_.isNil(bucket)) {
      return emptyArray;
    }

    return bucket.ids.map(id => slice.stepsById[id]).filter(Boolean);
  });

// Run-wide tool-activity counts derived from every step's loaded messages.
// Reuses the same FE-side parser/classifier the step detail view and planner
// rows use, so the snapshot never invents counts — it only reflects tool calls
// already parsed out of persisted step messages. Steps whose messages are not
// loaded yet contribute nothing; the drawer eager-loads them so it converges.
export const selectRunToolStats = executionId =>
  createSelector(selectSlice, slice => {
    const bucket = slice.stepsByRunId[executionId];

    if (_.isNil(bucket)) {
      return computeRunToolStats(emptyArray);
    }

    const stepsMessages = bucket.ids.map(stepId => {
      const messagesBucket = slice.messagesByStepId[stepId];

      if (_.isNil(messagesBucket)) {
        return emptyArray;
      }

      return messagesBucket.ids.map(id => slice.messagesById[id]).filter(Boolean);
    });

    return computeRunToolStats(stepsMessages);
  });

export const selectMessagesByStep = stepExecutionId =>
  createSelector(selectSlice, slice => {
    const bucket = slice.messagesByStepId[stepExecutionId];

    if (_.isNil(bucket)) {
      return emptyArray;
    }

    return bucket.ids.map(id => slice.messagesById[id]).filter(Boolean);
  });

export const selectStepMessagesStatus = stepExecutionId =>
  createSelector(
    selectSlice,
    slice => slice.messagesByStepId[stepExecutionId]?.status ?? LoadStatus.IDLE,
  );

export const selectStepById = stepExecutionId =>
  createSelector(selectSlice, slice => slice.stepsById[stepExecutionId] ?? null);

export const selectCitationsByStep = stepExecutionId =>
  createSelector(
    selectSlice,
    slice => slice.citationsByStepId[stepExecutionId]?.items ?? emptyArray,
  );

export const selectStepCitationsStatus = stepExecutionId =>
  createSelector(
    selectSlice,
    slice => slice.citationsByStepId[stepExecutionId]?.status ?? LoadStatus.IDLE,
  );

export const selectCitationsByRun = executionId =>
  createSelector(
    selectSlice,
    slice => slice.citationsByExecutionId?.[executionId]?.items ?? emptyArray,
  );

export const selectRunCitationsStatus = executionId =>
  createSelector(
    selectSlice,
    slice => slice.citationsByExecutionId?.[executionId]?.status ?? LoadStatus.IDLE,
  );

export const selectStepBlob = (stepExecutionId, kind) =>
  createSelector(selectSlice, slice => slice.blobsByStepId[stepExecutionId]?.[kind]?.value ?? null);

export const selectStepBlobStatus = (stepExecutionId, kind) =>
  createSelector(
    selectSlice,
    slice => slice.blobsByStepId[stepExecutionId]?.[kind]?.status ?? LoadStatus.IDLE,
  );

export const selectStepGuidelineFullView = createSelector(
  selectSlice,
  slice => slice.guidelineFullView ?? null,
);

export const selectStepGuidelineContent = createSelector(selectSlice, slice => {
  const citation = slice.guidelineFullView;

  return citation ? (slice.guidelineCache?.[citation.id] ?? null) : null;
});

export const selectStepIsLoadingGuideline = createSelector(
  selectSlice,
  slice => slice.isLoadingGuideline ?? false,
);

export const selectStepPendingToolCall = stepExecutionId =>
  createSelector(selectSlice, slice => slice.pendingToolCallByStepId?.[stepExecutionId] ?? null);

export const selectHasStepPendingToolCallEntry = stepExecutionId =>
  createSelector(selectSlice, slice =>
    Object.prototype.hasOwnProperty.call(slice.pendingToolCallByStepId ?? {}, stepExecutionId),
  );

export const selectStepPendingQuestion = stepExecutionId =>
  createSelector(selectSlice, slice => slice.pendingQuestionByStepId?.[stepExecutionId] ?? null);

export const selectHasStepPendingQuestionEntry = stepExecutionId =>
  createSelector(selectSlice, slice =>
    Object.prototype.hasOwnProperty.call(slice.pendingQuestionByStepId ?? {}, stepExecutionId),
  );

export const selectIsAnsweringStep = stepExecutionId =>
  createSelector(selectSlice, slice => !!slice.answeringStepIds?.[stepExecutionId]);

export const selectProcessesUsedByOwner = ownerId =>
  createSelector(selectSlice, slice => slice.processesByOwner[ownerId]?.items ?? emptyArray);

export const selectFiltersForOwner = ownerId =>
  createSelector(selectSlice, slice => slice.filtersByOwner[ownerId] ?? emptyFilters);

export const selectIsConfirmingStep = stepExecutionId =>
  createSelector(selectSlice, slice => !!slice.confirmingStepIds?.[stepExecutionId]);

export const selectIsRetryingStep = stepExecutionId =>
  createSelector(selectSlice, slice => !!slice.retryingStepIds?.[stepExecutionId]);

export const selectIsRetryingFromHereStep = stepExecutionId =>
  createSelector(selectSlice, slice => !!slice.retryingFromHereStepIds?.[stepExecutionId]);

export const selectFormByExecution = executionId =>
  createSelector(selectSlice, slice => slice.formByExecutionId?.[executionId] ?? null);

export const selectHasFormEntry = executionId =>
  createSelector(selectSlice, slice =>
    Object.prototype.hasOwnProperty.call(slice.formByExecutionId ?? {}, executionId),
  );

export const selectIsFormActionInFlight = formId =>
  createSelector(selectSlice, slice => !!slice.formActionInFlightById?.[formId]);

// Execution ids whose AI form is awaiting a human review. Keying off the form
// slice (formByExecutionId) keeps this driven by the same state the drawer and
// live SignalR events already maintain — no extra fetch path.
export const selectExecutionIdsNeedingReview = createSelector(selectSlice, slice => {
  const byExecution = slice.formByExecutionId ?? {};
  const ids = Object.keys(byExecution).filter(
    executionId => byExecution[executionId]?.status === AiFormStatus.READY_FOR_REVIEW,
  );

  return ids.length === 0 ? emptySet : new Set(ids);
});

export const selectNeedsReviewCount = createSelector(
  selectExecutionIdsNeedingReview,
  ids => ids.size,
);
